import React, { useEffect, useRef } from 'react';

import { getChannelDisplayName } from '../../../lib/channels';

import { useRouteWorkspace } from '../../../contexts/RouteWorkspaceContext';
import useChannelConfig from '../../../hooks/useChannelConfig';
import { useDocumentTitleContext } from '../../../hooks/useDocumentTitleEvents';

import { BootSkeleton } from '../../../feed/ui/BootSkeleton';
import ChatSurface from './ChatSurface';

const ChatView = ({ channelId }) => {
  const { setTitle } = useDocumentTitleContext();
  const { mainChannelId, workspaceId } = useRouteWorkspace();
  const channelConfig = useChannelConfig(channelId);
  const ready = channelConfig.status === 'ready' && String(channelConfig.channel.id) === String(channelId);
  const lastReadyConfigRef = useRef(null);

  if (ready) {
    lastReadyConfigRef.current = {
      channel: channelConfig.channel,
      breadcrumbs: channelConfig.breadcrumbs,
    };
  } else if (lastReadyConfigRef.current && String(lastReadyConfigRef.current.channel.workspace_id) !== String(workspaceId)) {
    // Retention only bridges channel switches within a workspace. Across a
    // workspace switch the layout stays mounted (route bundle cache), and a
    // retained surface would run the old workspace's channel and composer
    // under the new workspace's context.
    lastReadyConfigRef.current = null;
  }

  useEffect(() => {
    if (!ready) return;

    const isMainChannel = String(channelConfig.channel.id) === String(mainChannelId);
    setTitle(isMainChannel ? 'Chat' : getChannelDisplayName(channelConfig.channel));
  }, [channelConfig, mainChannelId, ready, setTitle]);

  useEffect(() => {
    if (channelConfig.status !== 'unavailable') return;
    window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: {} }));
  }, [channelConfig.status]);

  if (channelConfig.status === 'idle' || channelConfig.status === 'unavailable') return null;

  const config = lastReadyConfigRef.current;

  if (!config) {
    return (
      <div className="relative size-full">
        <BootSkeleton />
      </div>
    );
  }

  return (
    <div className="size-full">
      <ChatSurface
        key={String(config.channel.id)}
        currentChannel={config.channel}
        currentChannelBreadcrumbs={config.breadcrumbs}
      />
    </div>
  );
};

export default ChatView;
